import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';

import { logout } from '../api/auth';
import { getTermsAcceptance, type TermsAcceptanceState } from '../api/legal';
import { useSession } from '../session';
import { TermsAcceptancePanel } from './TermsAcceptancePanel';

/**
 * The blocking terms gate (owner decision 2026-10-08).
 *
 * After sign-in, and again whenever the server's current terms version changes, a signed-in person
 * sees only the acceptance screen until they accept. This is the convenience layer: the server
 * refuses workspace creation, workspace entry, relay generation and hosted tasks with
 * `403 terms_acceptance_required` on its own, whatever this screen does.
 *
 * `checking` blocks (briefly) so protected pages do not flash; `unavailable` (the status could not be
 * read) does not block, because the server gate still holds and a failed read must not lock a
 * customer out of billing, settings and support.
 */
export type TermsGateStatus = 'checking' | 'accepted' | 'required' | 'not_enforced' | 'unavailable';

interface TermsGateState {
  status: TermsGateStatus;
  terms: TermsAcceptanceState | null;
  recheck: () => Promise<void>;
  markAccepted: (state: TermsAcceptanceState) => void;
}

const TermsGateContext = createContext<TermsGateState | null>(null);

export function termsGateStatus(state: TermsAcceptanceState): TermsGateStatus {
  if (state.accepted) return 'accepted';
  return state.required ? 'required' : 'not_enforced';
}

export function TermsGateProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [status, setStatus] = useState<TermsGateStatus>('checking');
  const [terms, setTerms] = useState<TermsAcceptanceState | null>(null);
  const identity = session ? `${session.org_id}\u001f${session.principal_id}` : '';

  const recheck = useCallback(async () => {
    if (!identity) return;
    setStatus('checking');
    try {
      const state = await getTermsAcceptance();
      setTerms(state);
      setStatus(termsGateStatus(state));
    } catch {
      setTerms(null);
      setStatus('unavailable');
    }
  }, [identity]);

  useEffect(() => {
    setTerms(null);
    setStatus('checking');
    if (identity) void recheck();
  }, [identity, recheck]);

  // A version change mid-session: re-read quietly on navigation, so the next page shows the gate
  // again without a loading flash. A failed quiet read changes nothing (the server still enforces).
  const [location] = useLocation();
  useEffect(() => {
    if (!identity || status !== 'accepted') return;
    let active = true;
    getTermsAcceptance()
      .then((state) => {
        if (!active) return;
        setTerms(state);
        setStatus(termsGateStatus(state));
      })
      .catch(() => {});
    return () => { active = false; };
    // Only navigation triggers a quiet re-read; status changes are handled by recheck/markAccepted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, identity]);

  const markAccepted = useCallback((state: TermsAcceptanceState) => {
    setTerms(state);
    setStatus(termsGateStatus(state));
  }, []);

  const value = useMemo(() => ({ status, terms, recheck, markAccepted }), [status, terms, recheck, markAccepted]);
  return <TermsGateContext.Provider value={value}>{children}</TermsGateContext.Provider>;
}

/** `null` outside a provider (route tests render pages without one): nothing is gated there. */
export function useTermsGate(): TermsGateState | null {
  return useContext(TermsGateContext);
}

export function TermsGateScreen({ onAccepted }: { onAccepted: (state: TermsAcceptanceState) => void }) {
  const { setSession } = useSession();
  const [, navigate] = useLocation();
  const [signingOut, setSigningOut] = useState(false);

  async function signOut() {
    setSigningOut(true);
    try {
      await logout();
    } catch {
      // The session may already be gone; the login page is the right place either way.
    }
    setSession(null);
    navigate('/login', { replace: true });
  }

  return (
    <section className="terms-gate-page" aria-label="Accept the current terms">
      <p className="eyebrow">Before you continue</p>
      <TermsAcceptancePanel mode="gate" onContinue={(state) => { if (state) onAccepted(state); }} />
      <p>
        <button type="button" className="secondary" disabled={signingOut} onClick={() => void signOut()}>
          {signingOut ? 'Signing out…' : 'Sign out instead'}
        </button>
      </p>
    </section>
  );
}

/** Wraps a protected page: renders the gate screen instead of the page until terms are accepted. */
export function TermsGate({ children }: { children: ReactNode }) {
  const gate = useTermsGate();
  if (!gate) return <>{children}</>;
  if (gate.status === 'checking') return <p className="route-state" role="status">Checking terms…</p>;
  if (gate.status === 'required') return <TermsGateScreen onAccepted={gate.markAccepted} />;
  return <>{children}</>;
}
